import { useEffect } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { notificationsApiRef } from '@backstage/plugin-notifications';
import { useSignal } from '@backstage/plugin-signals-react';
import { IncidentPushAuto } from '@internal/backstage-plugin-platform-pipeline';
import { toWebNotification } from './actions';

const WORKER_URL = '/sw.js';
/** Topic of the incident notifications (DevNotifier TOPICS.incidents), also sent by Web Push. */
const INCIDENTS_TOPIC = 'Incidents plateforme';

interface NotificationSignal {
  action?: string;
  notification_id?: string;
  [key: string]: string | undefined;
}

/**
 * Browser (OS) notifications for Backstage notifications, with buttons.
 *
 * Replaces the sidebar bell's built-in `webNotificationsEnabled`, which uses `new Notification()`
 * and therefore cannot show buttons: those need a service worker (public/sw.js). Buttons come
 * from `metadata.actions` set by the backend. Without service worker support (insecure context,
 * old browser) it falls back to a plain notification, without buttons.
 */
export function ActionableWebNotifications() {
  const notificationsApi = useApi(notificationsApiRef);
  const { lastSignal } = useSignal<NotificationSignal>('notifications');

  // Worker + permission. The permission prompt needs a user gesture, so ask on the first click.
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return undefined;
    navigator.serviceWorker?.register(WORKER_URL).catch(() => undefined);

    const ask = () => {
      if (window.Notification.permission === 'default') window.Notification.requestPermission().catch(() => undefined);
    };
    document.addEventListener('click', ask, { once: true, capture: true });

    // A click on a notification (or one of its buttons) marks it as read: the worker has no credentials.
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'notification-click' && typeof event.data.id === 'string') {
        notificationsApi.updateNotifications({ ids: [event.data.id], read: true }).catch(() => undefined);
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => {
      document.removeEventListener('click', ask, { capture: true });
      navigator.serviceWorker?.removeEventListener('message', onMessage);
    };
  }, [notificationsApi]);

  useEffect(() => {
    if (lastSignal?.action !== 'new_notification' || !lastSignal.notification_id) return undefined;
    if (typeof window === 'undefined' || !('Notification' in window) || window.Notification.permission !== 'granted') {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const notification = await notificationsApi.getNotification(lastSignal.notification_id!);
      if (cancelled || !notification) return;
      const { title, options } = toWebNotification(notification, window.location.origin);
      const registration = await navigator.serviceWorker?.getRegistration(WORKER_URL);
      // Incidents also come by Web Push to this browser when it subscribed: show them once.
      if (
        notification.payload.topic === INCIDENTS_TOPIC &&
        (await registration?.pushManager?.getSubscription())
      ) {
        return;
      }
      if (registration) {
        await registration.showNotification(title, options);
        return;
      }
      const { actions: _buttons, ...plainOptions } = options;
      const plain = new window.Notification(title, plainOptions);
      plain.onclick = () => window.open(options.data.link, '_blank');
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [lastSignal, notificationsApi]);

  // Incident pushes turn on with the permission asked above, without a visit to /incidents.
  return <IncidentPushAuto />;
}
